import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { Facebook, Instagram, WhatsApp, Close } from "@material-ui/icons";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import { Menu, MenuItem } from "@material-ui/core";
import { ArrowDropDown, Backup, ContactPhone, DeleteSweep } from "@material-ui/icons";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import {
  LuPencil,
  LuTrash2,
  LuBan,
  LuCheckCircle2,
  LuXCircle,
} from "react-icons/lu";
import { v4 as uuidv4 } from "uuid";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ContactDeleteConfirmModal from "../../components/ContactDeleteConfirmModal";
import {
  CrudListPage,
  CrudSearchField,
  CrudActionButton,
  useCrudListStyles,
  useModernDialogStyles,
} from "../../components/CrudListPage";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";

import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import formatSerializedId from "../../utils/formatSerializedId";

import ContactImportWpModal from "../../components/ContactImportWpModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    }
    return [contact, ...state];
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "BULK_DELETE_CONTACTS") {
    const contactIds = action.payload;
    return state.filter((contact) => !contactIds.includes(contact.id));
  }

  if (action.type === "DELETE_ALL_CONTACTS") {
    const { excludeIds = [] } = action.payload;
    return state.filter((contact) => excludeIds.includes(contact.id));
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "SET_TOTAL_COUNT") {
    return state;
  }

  return state;
};

const useLocalStyles = makeStyles((theme) => ({
  avatarCell: {
    width: 60,
    padding: theme.spacing(1),
  },
  idCell: {
    width: 80,
  },
  checkboxCell: {
    width: 48,
    padding: theme.spacing(0, 1),
  },
  clickableAvatar: {
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out",
    border: `2px solid ${theme.palette.divider}`,
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  profileImage: {
    width: "100%",
    height: "auto",
    maxWidth: 400,
    maxHeight: 400,
    objectFit: "contain",
    borderRadius: 8,
  },
  imagePlaceholder: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
    color: theme.palette.text.secondary,
  },
  dialogTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: theme.spacing(1),
  },
  bulkToolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    minHeight: 48,
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  bulkToolbarHighlight: {
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
  },
  bulkToolbarTitle: {
    flex: "1 1 200px",
    fontWeight: 600,
    fontSize: "0.875rem",
  },
  bulkActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  selectionHint: {
    color: theme.palette.secondary.main,
    marginLeft: theme.spacing(1),
    fontSize: "1rem",
    fontWeight: 500,
  },
  statusActive: {
    color: theme.palette.success.main,
  },
  statusInactive: {
    color: theme.palette.error.main,
  },
  dangerMenuItem: {
    color: theme.palette.error.main,
  },
  dangerMenuIcon: {
    marginRight: 10,
    color: theme.palette.error.main,
  },
  menuIcon: {
    marginRight: 10,
  },
  channelAction: {
    padding: 8,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-disabled": {
      opacity: 0.35,
    },
  },
}));

const Contacts = () => {
  const classes = useCrudListStyles({ tableMinWidth: 1200 });
  const localClasses = useLocalStyles();
  const dialogClasses = useModernDialogStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [totalContactsCount, setTotalContactsCount] = useState(0);

  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);

  const [importContactModalOpen, setImportContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [ImportContacts, setImportContacts] = useState(null);
  const [blockingContact, setBlockingContact] = useState(null);
  const [unBlockingContact, setUnBlockingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportContact, setExportContact] = useState(false);
  const [confirmChatsOpen, setConfirmChatsOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("");

  const fileUploadRef = useRef(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const { setCurrentTicket } = useContext(TicketsContext);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedContactName, setSelectedContactName] = useState("");

  const { getAll: getAllSettings } = useCompanySettings();
  const [hideNum, setHideNum] = useState(false);
  const [enableLGPD, setEnableLGPD] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (!user?.companyId) return;
      const settingList = await getAllSettings(user.companyId);

      for (const [key, value] of Object.entries(settingList)) {
        if (key === "enableLGPD") setEnableLGPD(value === "enabled");
        if (key === "lgpdHideNumber") setHideNum(value === "enabled");
      }
    }
    fetchData();
  }, [user?.companyId]);

  const handleSelectContact = (contactId) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedContacts(newSelected);
    setSelectAllMode(false);
  };

  const handleSelectAllContacts = () => {
    if (selectAllMode) {
      setSelectedContacts(new Set());
      setSelectAllMode(false);
    } else {
      const currentPageIds = contacts.map((contact) => contact.id);
      const allCurrentSelected = currentPageIds.every((id) =>
        selectedContacts.has(id)
      );

      if (allCurrentSelected && selectedContacts.size === currentPageIds.length) {
        setSelectAllMode(true);
        setSelectedContacts(new Set());
      } else {
        setSelectedContacts(new Set(currentPageIds));
        setSelectAllMode(false);
      }
    }
  };

  useEffect(() => {
    setSelectedContacts(new Set());
    setSelectAllMode(false);
  }, [searchParam, selectedTags]);

  const getSelectedCount = () => {
    if (selectAllMode) {
      return totalContactsCount;
    }
    return selectedContacts.size;
  };

  const getSelectionButtonText = () => {
    if (selectAllMode) {
      return `Excluir Todos (${totalContactsCount})`;
    }
    if (selectedContacts.size > 0) {
      return `Excluir Selecionados (${selectedContacts.size})`;
    }
    return "Excluir";
  };

  const handleBulkDeleteClick = () => {
    if (selectAllMode) {
      setDeleteType("all");
    } else if (selectedContacts.size > 0) {
      setDeleteType("selected");
    } else {
      toast.warning("Selecione pelo menos um contato para excluir");
      return;
    }
    setDeleteConfirmModalOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      if (deleteType === "all") {
        await api.delete("/contacts/all", {
          data: {
            confirmation: "DELETE_ALL_CONTACTS",
            excludeIds: [],
          },
        });
        toast.success(`Todos os ${totalContactsCount} contatos foram excluídos`);
      } else {
        const contactIds = Array.from(selectedContacts);
        await api.post("/contacts/bulk-delete", { contactIds });
        toast.success(`${contactIds.length} contatos excluídos com sucesso`);
      }

      setSelectedContacts(new Set());
      setSelectAllMode(false);
      setSearchParam("");
      setPageNumber(1);
    } catch (err) {
      toastError(err);
    } finally {
      setDeleteConfirmModalOpen(false);
      setDeleteType("");
    }
  };

  const handleDeleteAllClick = () => {
    setDeleteType("all");
    setDeleteConfirmModalOpen(true);
  };

  const handleOpenImageModal = (imageUrl, contactName) => {
    setSelectedImage(imageUrl);
    setSelectedContactName(contactName);
    setImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage("");
    setSelectedContactName("");
  };

  const handleImportExcel = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({
        url: `/contacts/upload`,
        method: "POST",
        data: formData,
      });
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, selectedTags]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: {
              searchParam,
              pageNumber,
              contactTag: JSON.stringify(selectedTags),
            },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setTotalContactsCount(data.count);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, selectedTags]);

  useEffect(() => {
    if (!socket || !user?.companyId) return;

    const contactEvent = `company-${user.companyId}-contact`;

    const onContactEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
        setTotalContactsCount((prev) => Math.max(0, prev - 1));
      }

      if (data.action === "bulk-delete") {
        dispatch({ type: "BULK_DELETE_CONTACTS", payload: data.contactIds });
        setTotalContactsCount((prev) =>
          Math.max(0, prev - data.contactIds.length)
        );
      }

      if (data.action === "delete-all") {
        dispatch({
          type: "DELETE_ALL_CONTACTS",
          payload: { excludeIds: data.excludeIds },
        });
        setTotalContactsCount(data.excludeIds.length);
      }
    };

    socket.on(contactEvent, onContactEvent);

    return () => {
      socket.off(contactEvent, onContactEvent);
    };
  }, [socket, user?.companyId]);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: false });
      toast.success("Contato bloqueado");
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
    setBlockingContact(null);
  };

  const handleUnBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: true });
      toast.success("Contato desbloqueado");
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
    setUnBlockingContact(null);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      history.go(0);
      setImportContacts(false);
    } catch (err) {
      toastError(err);
      setImportContacts(false);
    }
  };

  const handleimportChats = async () => {
    try {
      await api.post("/contacts/import/chats");
      history.go(0);
    } catch (err) {
      toastError(err);
    }
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber((prevState) => prevState + 1);
    }
  };

  const getConfirmAction = () => {
    if (deletingContact) return () => handleDeleteContact(deletingContact.id);
    if (blockingContact) return () => handleBlockContact(blockingContact.id);
    if (unBlockingContact) return () => handleUnBlockContact(unBlockingContact.id);
    if (ImportContacts) return handleimportContact;
    return handleImportExcel;
  };

  const getConfirmTitle = () => {
    if (deletingContact)
      return `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`;
    if (blockingContact) return `Bloquear Contato ${blockingContact.name}?`;
    if (unBlockingContact) return `Desbloquear Contato ${unBlockingContact.name}?`;
    if (ImportContacts) return i18n.t("contacts.confirmationModal.importTitlte");
    return i18n.t("contactListItems.confirmationModal.importTitlte");
  };

  const getConfirmMessage = () => {
    if (exportContact) return i18n.t("contacts.confirmationModal.exportContact");
    if (deletingContact) return i18n.t("contacts.confirmationModal.deleteMessage");
    if (blockingContact) return i18n.t("contacts.confirmationModal.blockContact");
    if (unBlockingContact) return i18n.t("contacts.confirmationModal.unblockContact");
    if (ImportContacts) return i18n.t("contacts.confirmationModal.importMessage");
    return i18n.t("contactListItems.confirmationModal.importMessage");
  };

  const formatContactNumber = (contact) => {
    if (enableLGPD && hideNum && user?.profile === "user") {
      if (contact.isGroup) return contact.number;
      if (formatSerializedId(contact?.number) === null) {
        return (
          contact.number.slice(0, -6) + "**-**" + contact?.number.slice(-2)
        );
      }
      return (
        formatSerializedId(contact?.number)?.slice(0, -6) +
        "**-**" +
        contact?.number?.slice(-2)
      );
    }
    if (contact.isGroup) return contact.number;
    return formatSerializedId(contact?.number);
  };

  const selectedCount = getSelectedCount();
  const isAnyContactSelected = selectAllMode || selectedContacts.size > 0;

  const selectAllCheckboxStatus = () => {
    if (selectAllMode) return { checked: true, indeterminate: false };

    const currentPageIds = contacts.map((contact) => contact.id);
    const selectedInCurrentPage = currentPageIds.filter((id) =>
      selectedContacts.has(id)
    ).length;

    if (selectedInCurrentPage === 0) {
      return { checked: false, indeterminate: false };
    }
    if (selectedInCurrentPage === currentPageIds.length) {
      return { checked: true, indeterminate: false };
    }
    return { checked: false, indeterminate: true };
  };

  const checkboxStatus = selectAllCheckboxStatus();

  const pageTitle = (
    <>
      {`${i18n.t("contacts.title")} (${totalContactsCount})`}
      {isAnyContactSelected && (
        <Typography component="span" className={localClasses.selectionHint}>
          - {selectedCount} selecionado(s) {selectAllMode && "(TODOS)"}
        </Typography>
      )}
    </>
  );

  return (
    <MainContainer>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      />
      <ContactDeleteConfirmModal
        open={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        deleteType={deleteType}
        selectedCount={selectedContacts.size}
        totalCount={totalContactsCount}
      />
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseImageModal}
        maxWidth="md"
        classes={{ paper: dialogClasses.paper }}
      >
        <DialogTitle className={dialogClasses.title}>
          <div className={localClasses.dialogTitleRow}>
            <span>Foto de Perfil - {selectedContactName}</span>
            <IconButton onClick={handleCloseImageModal} size="small" aria-label="Fechar">
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className={dialogClasses.content}>
          {selectedImage ? (
            <img
              src={selectedImage}
              alt={`Foto de perfil de ${selectedContactName}`}
              className={localClasses.profileImage}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div className={localClasses.imagePlaceholder}>
              Imagem não disponível
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmationModal
        title={getConfirmTitle()}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={getConfirmAction()}
      >
        {getConfirmMessage()}
      </ConfirmationModal>
      <ConfirmationModal
        title={i18n.t("contacts.confirmationModal.importChat")}
        open={confirmChatsOpen}
        onClose={setConfirmChatsOpen}
        onConfirm={() => handleimportChats()}
      >
        {i18n.t("contacts.confirmationModal.wantImport")}
      </ConfirmationModal>
      {importContactModalOpen && (
        <ContactImportWpModal
          isOpen={importContactModalOpen}
          handleClose={() => setImportContactModalOpen(false)}
          selectedTags={selectedTags}
          hideNum={hideNum}
          userProfile={user?.profile}
        />
      )}

      <input
        style={{ display: "none" }}
        id="upload"
        name="file"
        type="file"
        accept=".xls,.xlsx"
        onChange={() => {
          setConfirmOpen(true);
        }}
        ref={fileUploadRef}
      />

      <CrudListPage
        title={pageTitle}
        tableMinWidth={1200}
        toolbarWide
        onScroll={handleScroll}
        toolbar={
          <>
            <div className={`${classes.toolbarItem} ${classes.tagsFilterField}`}>
              <TagsFilter onFiltered={handleSelectedTags} />
            </div>
            <div className={classes.toolbarItem}>
              <CrudSearchField
                classes={classes}
                value={searchParam}
                onChange={handleSearch}
                placeholder={i18n.t("contacts.searchPlaceholder")}
              />
            </div>
            {isAnyContactSelected && (
              <div className={classes.toolbarItem}>
                <Tooltip title={selectAllMode ? "Excluir todos os contatos" : "Excluir contatos selecionados"}>
                  <Button
                    variant="contained"
                    color="secondary"
                    className={classes.secondaryButton}
                    onClick={handleBulkDeleteClick}
                  >
                    {getSelectionButtonText()}
                  </Button>
                </Tooltip>
              </div>
            )}
            <div className={classes.toolbarItem}>
              <PopupState variant="popover" popupId="contacts-import-export-menu">
                {(popupState) => (
                  <React.Fragment>
                    <Button
                      variant="outlined"
                      color="primary"
                      className={classes.secondaryButton}
                      {...bindTrigger(popupState)}
                    >
                      {i18n.t("contacts.menu.importexport")}
                      <ArrowDropDown />
                    </Button>
                  <Menu {...bindMenu(popupState)}>
                    <MenuItem
                      onClick={() => {
                        setConfirmOpen(true);
                        setImportContacts(true);
                        popupState.close();
                      }}
                    >
                      <ContactPhone
                        fontSize="small"
                        color="primary"
                        className={localClasses.menuIcon}
                      />
                      {i18n.t("contacts.menu.importYourPhone")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setImportContactModalOpen(true);
                        popupState.close();
                      }}
                    >
                      <Backup
                        fontSize="small"
                        color="primary"
                        className={localClasses.menuIcon}
                      />
                      {i18n.t("contacts.menu.importToExcel")}
                    </MenuItem>
                    <Can
                      role={user?.profile}
                      perform="contacts-page:deleteAllContacts"
                      yes={() => (
                        <MenuItem
                          onClick={() => {
                            handleDeleteAllClick();
                            popupState.close();
                          }}
                          className={localClasses.dangerMenuItem}
                        >
                          <DeleteSweep
                            fontSize="small"
                            className={localClasses.dangerMenuIcon}
                          />
                          Excluir Todos os Contatos
                        </MenuItem>
                      )}
                    />
                  </Menu>
                </React.Fragment>
              )}
              </PopupState>
            </div>
            <div className={classes.toolbarItem}>
              <Button
                variant="contained"
                color="primary"
                className={classes.primaryButton}
                onClick={handleOpenContactModal}
              >
                {i18n.t("contacts.buttons.add")}
              </Button>
            </div>
          </>
        }
      >
        {isAnyContactSelected && (
          <Toolbar
            className={`${localClasses.bulkToolbar} ${localClasses.bulkToolbarHighlight}`}
          >
            <Typography className={localClasses.bulkToolbarTitle} color="inherit">
              {selectedCount} contato(s) selecionado(s){" "}
              {selectAllMode && "(TODOS OS CONTATOS)"}
            </Typography>
            <div className={localClasses.bulkActions}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedContacts(new Set());
                  setSelectAllMode(false);
                }}
              >
                Cancelar
              </Button>
              <Tooltip title={selectAllMode ? "Excluir todos os contatos" : "Excluir selecionados"}>
                <IconButton color="inherit" onClick={handleBulkDeleteClick} aria-label="Excluir selecionados">
                  <LuTrash2 size={18} strokeWidth={1.75} />
                </IconButton>
              </Tooltip>
            </div>
          </Toolbar>
        )}

        <Table size="small" stickyHeader className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={localClasses.checkboxCell}>
                <Checkbox
                  indeterminate={checkboxStatus.indeterminate}
                  checked={checkboxStatus.checked}
                  onChange={handleSelectAllContacts}
                  disabled={contacts.length === 0}
                  inputProps={{ "aria-label": "Selecionar todos os contatos" }}
                />
              </TableCell>
              <TableCell className={localClasses.idCell}>ID</TableCell>
              <TableCell className={localClasses.avatarCell} align="center">
                Foto
              </TableCell>
              <TableCell>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.email")}
              </TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.wallet")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => {
              const isSelected =
                selectAllMode || selectedContacts.has(contact.id);

              return (
                <TableRow key={contact.id} selected={isSelected}>
                  <TableCell className={localClasses.checkboxCell}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleSelectContact(contact.id)}
                      disabled={selectAllMode}
                      inputProps={{
                        "aria-label": `Selecionar contato ${contact.name}`,
                      }}
                    />
                  </TableCell>
                  <TableCell className={localClasses.idCell}>
                    {contact.id}
                  </TableCell>
                  <TableCell className={localClasses.avatarCell} align="center">
                    <Avatar
                      src={`${contact?.urlPicture}`}
                      className={localClasses.clickableAvatar}
                      onClick={() =>
                        handleOpenImageModal(contact?.urlPicture, contact.name)
                      }
                    />
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell align="center">
                    {formatContactNumber(contact)}
                  </TableCell>
                  <TableCell align="center">{contact.email}</TableCell>
                  <TableCell align="center">
                    {contact.active ? (
                      <LuCheckCircle2
                        size={18}
                        strokeWidth={1.75}
                        className={localClasses.statusActive}
                        aria-label="Ativo"
                      />
                    ) : (
                      <LuXCircle
                        size={18}
                        strokeWidth={1.75}
                        className={localClasses.statusInactive}
                        aria-label="Inativo"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {contact.contactWallets && contact.contactWallets.length > 0
                      ? contact.contactWallets[0].wallet?.name ||
                        "Usuário não encontrado"
                      : "Não atribuído"}
                  </TableCell>
                  <TableCell align="center" className={classes.actionsCell}>
                    <IconButton
                      size="small"
                      className={localClasses.channelAction}
                      disabled={!contact.active}
                      onClick={() => {
                        setContactTicket(contact);
                        setNewTicketModalOpen(true);
                      }}
                      aria-label="Abrir ticket"
                    >
                      {contact.channel === "whatsapp" && (
                        <WhatsApp style={{ color: "#25d366" }} fontSize="small" />
                      )}
                      {contact.channel === "instagram" && (
                        <Instagram style={{ color: "#e1306c" }} fontSize="small" />
                      )}
                      {contact.channel === "facebook" && (
                        <Facebook style={{ color: "#3b5998" }} fontSize="small" />
                      )}
                    </IconButton>
                    <CrudActionButton
                      classes={classes}
                      icon={LuPencil}
                      onClick={() => hadleEditContact(contact.id)}
                      ariaLabel="Editar contato"
                    />
                    <CrudActionButton
                      classes={classes}
                      icon={contact.active ? LuBan : LuCheckCircle2}
                      onClick={() => {
                        setConfirmOpen(true);
                        if (contact.active) {
                          setBlockingContact(contact);
                        } else {
                          setUnBlockingContact(contact);
                        }
                      }}
                      ariaLabel={
                        contact.active ? "Bloquear contato" : "Desbloquear contato"
                      }
                    />
                    <Can
                      role={user?.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <CrudActionButton
                          classes={classes}
                          icon={LuTrash2}
                          onClick={() => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                          ariaLabel="Excluir contato"
                          danger
                        />
                      )}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {loading &&
              Array.from({ length: 5 }).map((_, idx) => (
                <TableRowSkeleton
                  key={`skeleton-${idx}`}
                  avatar
                  columns={8}
                />
              ))}
          </TableBody>
        </Table>
      </CrudListPage>
    </MainContainer>
  );
};

export default Contacts;
